// import './application.css';
import React, { useState, useEffect } from 'react';
import { Link as ReactRouterLink } from 'react-router-dom';
import { format } from 'date-fns';
import {
  Box,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import BusinessIcon from '@mui/icons-material/Business';

import { getCommercialApplications } from '../../../services/commercial-applications.service';
import { onError } from '../../../utils/error.util';
import { useAppContext } from '../../../utils/content.util';
import { CircularProgressComponent } from '../../../components/admin/circular-progress.component';
import SearchBox from '../../../components/admin/search-box/search-box.component';
import { CommercialApplication } from '../../../models/commercial-application.model';

interface CommercialApplicationTableProps {
  data: CommercialApplication[];
  page: number;
  rowsPerPage: number;
  handleChangeRowsPerPage: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleChangePage: (event: unknown, newPage: number) => void;
  currentUser: any;
}

function CommercialApplicationTable({
  data,
  page,
  rowsPerPage = 300,
  handleChangeRowsPerPage,
  handleChangePage,
  currentUser,
}: CommercialApplicationTableProps) {
  return (
    <React.Fragment>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell> </TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Viewed</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Created</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Business Name</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Contact</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Business Location</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Phone</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Amount</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((row: CommercialApplication) => (
              <CommercialApplicationTableRow key={row.id} row={row} currentUser={currentUser} />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[300]}
        component="div"
        count={data.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </React.Fragment>
  );
}

interface CommercialApplicationTableRowProps {
  row: CommercialApplication;
  currentUser: any;
}

function CommercialApplicationTableRow({ row, currentUser }: CommercialApplicationTableRowProps) {
  const formatAmount = (amount: string) => {
    if (!amount) return '-';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(parseFloat(amount));
  };

  return (
    <TableRow selected={row.isArchived}>
      <TableCell>
        <Tooltip title="View Application">
          <ReactRouterLink key={row.id} to={`/commercial-forms/${row.id}`}>
            <VisibilityIcon color="primary" fontSize="small" />
          </ReactRouterLink>
        </Tooltip>
      </TableCell>
      <TableCell>{row.viewedBy || '-'}</TableCell>
      <TableCell sx={{ width: '115px' }}>
        {row.createdAt ? (
          <>
            {format(new Date(row.createdAt), 'MM-dd-yyyy')}
            <br />
            {format(new Date(row.createdAt), 'p')}
          </>
        ) : (
          '-'
        )}
      </TableCell>
      <TableCell>
        <Box>
          <div style={{ fontWeight: 'bold' }}>{row.dba || 'N/A'}</div>
          <div style={{ fontSize: '0.8em', color: 'gray' }}>
            {row.legalEntityType} • {row.yearsInBusiness} years
          </div>
        </Box>
      </TableCell>
      <TableCell>
        <Box>
          <div>
            {row.firstName} {row.lastName}
          </div>
          <div style={{ fontSize: '0.8em', color: 'gray' }}>{row.emailAddress}</div>
        </Box>
      </TableCell>
      <TableCell>
        <Box>
          <div>
            {row.businessCity}, {row.businessState}
          </div>
          <div style={{ fontSize: '0.8em', color: 'gray' }}>{row.businessAddressLine1}</div>
        </Box>
      </TableCell>
      <TableCell sx={{ width: '150px' }}>{row.primaryPhoneNumber}</TableCell>
      <TableCell sx={{ width: '120px' }} align="right">
        {formatAmount(row.amountRequested)}
      </TableCell>
    </TableRow>
  );
}

export default function CommercialFormsContainer() {
  const { isAuthenticated, currentUser } = useAppContext();

  const [applications, setApplications] = useState<CommercialApplication[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const [searchStartDate, setSearchStartDateValue] = useState('');
  const [searchEndDate, setSearchEndDateValue] = useState('');
  const [searchBusinessName, setSearchBusinessName] = useState('');
  // const [filterFinancingType, setFilterFinancingType] = useState<string>('all');
  const [shouldReload, setShouldReload] = useState(false);

  async function onLoad(name = '') {
    if (!isAuthenticated) {
      return;
    }

    setIsLoading(true);
    try {
      const applications = await getCommercialApplications({
        startDate: searchStartDate,
        endDate: searchEndDate,
        name: name || searchBusinessName,
        archived: false,
        isPhoneApplication: false,
      });
      setApplications(applications);
    } catch (e) {
      onError(e);
    }

    setIsLoading(false);
  }

  useEffect(() => {
    onLoad();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, currentUser]);

  useEffect(() => {
    if (shouldReload) {
      onLoad();
      setShouldReload(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldReload]);

  const handleSearch = (search: string) => {
    setSearchBusinessName(search);
    onLoad(search);
  };

  const handleSearchClear = () => {
    setSearchStartDateValue('');
    setSearchEndDateValue('');
    setSearchBusinessName('');
    setShouldReload(true);
  };

  function renderApplications() {
    return (
      <>
        <div className="applications">
          <h2 className="pb-2 mt-4 mb-3 border-bottom">
            <BusinessIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Commercial Forms
          </h2>
        </div>
        <div className="pb-2 mt-4 mb-3">
          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 2, flexWrap: 'wrap' }}>
            <TextField
              label="Start Date"
              type="date"
              slotProps={{ inputLabel: { shrink: true } }}
              value={searchStartDate}
              onChange={(e) => setSearchStartDateValue(e.target.value)}
              size="small"
            />

            <TextField
              label="End Date"
              type="date"
              slotProps={{ inputLabel: { shrink: true } }}
              value={searchEndDate}
              onChange={(e) => setSearchEndDateValue(e.target.value)}
              size="small"
            />

            <SearchBox
              placeholder="Search by business name or contact"
              handleSubmit={handleSearch}
              handleClearAll={handleSearchClear}
            />
          </Box>
        </div>
      </>
    );
  }

  return (
    <React.Fragment>
      <Grid container size={12}>
        <div className="Applications">
          {renderApplications()}
          {isLoading ? (
            <CircularProgressComponent />
          ) : (
            <CommercialApplicationTable
              data={applications}
              page={0}
              rowsPerPage={300}
              handleChangeRowsPerPage={() => {}}
              handleChangePage={() => {}}
              currentUser={currentUser}
            />
          )}
        </div>
      </Grid>
    </React.Fragment>
  );
}
